import React, { useState, useEffect } from "react";
import { useWorksiteUsers } from "@hooks/useWorksiteUsers";
import { useCheckKcolorProducts } from "@feature/commons/hooks/useCheckKcolorProducts";
import { FormattedMessage, useIntl } from "react-intl";
import Button from "@ui-components/Button";
import Modal from "@ui-components/Modal";
import Checkbox from "@ui-components/Checkbox";
import ConfiguratorSurveyTitle from "@components/ConfiguratorSurveyTitle";
import {fetchAgentEnterprises, fetchAgentResales, mapEnterprisesOptions, mapResalesOptions} from "../lib/utilities";
import { CreateQuotationModalProps } from "../types";
import { ServicesWithIdSchema } from "../../types";
import { UserReducedSchema } from "../../types";
import AuthenticationService from "@services/AuthenticationService";
import apiRequest from "@services/apiRequest";
import {UserOption} from "@feature/commons";
import {UserSelector} from "./components/user-selector";
import {QuotationEnterpriseSelector} from "../quotation/components/quotation-enterprise-selector";
import {canUserCreateQuotation} from "../lib/utilities";
import {useConnectedUsers} from "@feature/users/connections";
import {isValidEnterprise} from "@feature/quotations/lib/utilities";
import { SelectKcolorPriceModal } from "./components/select-kcolor-price-modal"
import {CompanyInfoTypeComponent} from "../../../components/Tables/Params/Connections";

export const CreateQuotationModal = ({
  worksite,
  creators,
  surveys,
  onDismiss,
  onConfirm,
}: CreateQuotationModalProps) => {

  /**
   * This component is responsible for creating a new quotation. It can be accessed only by the users that have a
   * quotation:create scope, and it is made up of two main parts:
   *
   * 1. A list of surveys that can be selected to create the quotation
   * 2. A form to select the users that will be involved in the quotation
   *
   * The users selectors are different based on the role of the user that is creating the quotation:
   * - Admins can select the agent, the enterprise, and the resale
   * - Agents can select resale and enterprise, and cannot select the agent since it's implied (the agent is the user
   * itself)
   * - Enterprises can only select the agent; the resale is not visible
   */

  // hooks
  const intl = useIntl();
  const userData = AuthenticationService.getUserData();
  const userScopes = AuthenticationService.getUserScopes();
  const accountUserScopes = AuthenticationService.getAccountUserScopes();
  const [worksiteUsers, _, loadingUsers]: [UserReducedSchema[], any, boolean] = useWorksiteUsers(worksite);
  const fetchKcolorProducts = useCheckKcolorProducts();
  const {
    fetch: fetchConnectedUsers,
    loading: loadingConnectedUsers,
  } = useConnectedUsers();

  if (!canUserCreateQuotation(accountUserScopes || userScopes)) {
    throw new Error("User not allowed to create a quotation");
  }

  // state
  const [loading, setLoading] = useState(false);
  // --- Surveys
  const [selectedSurveys, setSelectedSurveys]
    = useState<Set<number>>(new Set());
  // --- Users
  // ------ Agent
  const [selectedAgent, setSelectedAgent]
    = useState<UserOption | undefined>(
      userData.role_type === 'agent'
        ? {
          value: userData.id,
          label: `${userData.firstname} ${userData.lastname}`,
          // `object` prop will be automatically populated when the worksiteUsers are loaded
        } as UserOption  // set the agent as the user itself
        : undefined
    );
  // ------ Resale (default options + selected)
  const [resalesDefaultOptions, setResalesDefaultOptions]
    = useState<UserOption[]>([]);
  const [selectedResale, setSelectedResale]
    = useState<UserOption | undefined>(
      ['resale', 'resale_tintometer'].includes(userData.role_type)
        ? {
          value: userData.account_user_id || userData.id,
          label: (userData.account_user_company ?? (
                userData.account_user_firstname || userData.account_user_lastname ?
              `${userData.account_user_firstname || ''} ${userData.account_user_lastname || ''}` : undefined
              )) ||
              (userData.company ?? `${userData.firstname} ${userData.lastname}`
          ),
          // `object` prop will be automatically populated when the worksiteUsers are loaded
        } as UserOption  // set the resale as the user itself
        : undefined
      );
  // ------ Enterprise (default options + selected)
  const [enterprisesDefaultOptions, setEnterprisesDefaultOptions]
    = useState<UserOption[]>([]);
  const [selectedEnterprise, setSelectedEnterprise]
    = useState<UserOption | undefined>(
      (userData.account_user_role_type || userData.role_type) === 'enterprise' ? {
        value: userData.id,
        label: userData.company ?? `${userData.firstname} ${userData.lastname}`,
        // `object` prop will be automatically populated when the worksiteUsers are loaded
      } as UserOption  // set the enterprise as the user itself
      : undefined
  );
  const [hasKcolorProducts, setHasKcolorProducts] = useState<boolean>(false);

  // utilities
  const agentOptions = worksiteUsers
    ?.filter((u: any) => u.role === "agent")
    ?.map((u: any) => ({
      value: u.id as number,
      label: u.name,
      object: u,
    } as UserOption))
    ?? [];

  const groupedSurveys: { [key: string]: ServicesWithIdSchema[] } =
    surveys.reduce((acc, curr) => {
      const { services, ...rest } = curr;
      if (!services?.active)
        return acc;
      const name = services!.name;
      // @ts-ignore
      return { ...acc, [name]: [...(acc[name] ?? []), rest] };
    }, {});

  const onSubmit = async (kColorPriceChoose: null | "tintometer" | "kerakoll" = null) => {
    setLoading(true);
    await onConfirm({
      surveys: surveys.filter((s) => selectedSurveys.has(s.id)),
      agent: selectedAgent.object,
      enterprise: selectedEnterprise?.object,
      resale: selectedResale?.object,
      ...(!!selectedEnterprise && !selectedEnterprise.object
        ? { enterpriseToBeInvited: selectedEnterprise }
        : {}),
      kColorPriceChoose,
    });
    setLoading(false);
  }

  useEffect(() => {
    // Load first N enterprises that are visible to the selected agent (whenever the agent changes). They will be
    // used to populate the enterprise selector
    if (!selectedAgent) {
      setEnterprisesDefaultOptions([]);
      setResalesDefaultOptions([]);
    }
    else {
      fetchAgentEnterprises(
        apiRequest,
        selectedAgent.value,
        undefined,
        10,
        false
      ).then((results) => {
        const dfo = mapEnterprisesOptions(results.items) as unknown as UserOption[];
        setEnterprisesDefaultOptions(dfo);
      })
      fetchAgentResales(
        apiRequest,
        selectedAgent.value,
        undefined,
        10,
        undefined,
        undefined,
        false
      ).then((results) => {
        const dro = mapEnterprisesOptions(results.items) as unknown as UserOption[];
        setResalesDefaultOptions(dro);
      });
    }
    if (userData.role_type !== 'enterprise' && userData.account_user_role_type !== 'enterprise')
      setSelectedEnterprise(undefined);
    if (userData.role_type !== 'resale' &&
      userData.role_type !== 'resale_tintometer')
      setSelectedResale(undefined);
  }, [selectedAgent])

  useEffect(() => {
    // Update pre-selected agent/resale (if available) with the object prop, when the worksiteUsers are loaded
    if (!worksiteUsers?.length) return;
    if (!!selectedAgent && !selectedAgent.object) {
      const agent = worksiteUsers
        .find((u) => u.id === selectedAgent.value);
      if (agent)
        setSelectedAgent({ ...selectedAgent, object: agent });
    }
    else if (!!selectedResale && !selectedResale.object) {
      const resale = worksiteUsers
        .find((u) => u.id === selectedResale.value);
      if (resale)
        setSelectedResale({ ...selectedResale, object: resale });
    }
    else if (!!selectedEnterprise && !selectedEnterprise.object) {
      const enterprise = worksiteUsers
        .find((u) => u.id === selectedEnterprise.value);

      if (enterprise)
        setSelectedEnterprise({ ...selectedEnterprise, object: enterprise });
    }
  }, [worksiteUsers]);

  const editorBody = (
    <>
      <div className="flex flex-col gap-4 my-6">
        {/* ---------- SURVEYS SELECTORS ---------- */}
        {/* ---------- Services list ---------- */}
        {Object.entries(groupedSurveys).map(
          ([serviceName, serviceSurveys], serviceIdx) => (
            <div key={serviceIdx}>
              <h3 className="text-base font-semibold uppercase mb-2">
                {serviceName}
              </h3>
              <div className="flex flex-col gap-1">
                {/* ---------- Survey list ---------- */}
                {serviceSurveys.map((survey, surveyIdx) => (
                  <div key={surveyIdx} className="flex gap-3 items-center">
                    <div className="mt-0.25">
                      <Checkbox
                        id={`survey-${serviceIdx}-${surveyIdx}`}
                        name={`survey-${serviceIdx}-${surveyIdx}`}
                        checked={selectedSurveys.has(survey.id)}
                        onChange={() => {
                          setSelectedSurveys((prev) => {
                            const local = new Set(prev);
                            if (local.has(survey.id)) local.delete(survey.id);
                            else local.add(survey.id);
                            return local;
                          });
                        }}
                      />
                    </div>
                    <ConfiguratorSurveyTitle
                      service={undefined}
                      {...{ survey, creators }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )
        )}
      </div>
      {selectedSurveys.size > 0 && (
        <div className="w-full flex flex-col gap-5">
          <hr className="my-2" />
          {/* ---------- USERS SELECTORS ---------- */}
          <div className="flex flex-col gap-2">
            <h3 className="font-semibold text-base">
              <FormattedMessage
                id="select_involved_users"
                defaultMessage="Seleziona gli utenti coinvolti"
              />
            </h3>
            <p className="text-xs">
              <FormattedMessage
                id="select_involved_users_helper"
                defaultMessage={
                  "ATTENZIONE: fatta eccezione per l'agente, che verrà notificato automaticamente ed avrà accesso " +
                  "diretto al preventivo, gli utenti dovranno essere invitati manualmente una volta creato il " +
                  "preventivo."
                }
              />
            </p>
          </div>
          {/* ---------- Agent selector ---------- */}
          <UserSelector
            label={intl.formatMessage({
              id: "select_agent",
              defaultMessage: "Seleziona agente",
            })}
            visible={true}
            blocked={userData.role_type === "agent"}
            disabled={loadingUsers}
            required={true}
            value={selectedAgent}
            onChange={setSelectedAgent}
            options={agentOptions}
            warnings={
              userData.role_type !== "agent"
                ? [
                    intl.formatMessage({
                      id: "quotation_agent_selector_helper",
                      defaultMessage:
                        "Se non trovi l'agente desiderato, assicurati di averlo invitato all'interno del cantiere",
                    }),
                  ]
                : undefined
            }
          />
          {/* ---------- Resale selector ---------- */}
          <UserSelector
            label={intl.formatMessage({
              id: "select_resale",
              defaultMessage: "Seleziona rivendita",
            })}
            visible={userData.role_type !== "enterprise"} // Enterprises cannot see the resale selector
            blocked={["resale", "resale_tintometer"].includes(userData.role_type)}
            disabled={loadingUsers}
            required={false}
            value={selectedResale}
            onChange={setSelectedResale}
            defaultOptions={resalesDefaultOptions}
            loadOptions={async (
              text: string,
              callback: (options: UserOption[]) => void
            ) => {
              if (!!selectedAgent) {
                const enterprises = await fetchAgentResales(
                  apiRequest,
                  selectedAgent.value,
                  text
                );
                const options = mapEnterprisesOptions(
                  enterprises.items
                ) as unknown as UserOption[];
                callback(options);
              } else callback([]);
            }}
            CustomOption={({ data: userOption, innerProps, innerRef }: any) => {
              const { object, label, secondaryLabel } = userOption;
              return (
                <article
                  ref={innerRef}
                  className="py-2 px-3 cursor-pointer hover:bg-blue-50"
                  {...innerProps}
                >
                  {!!object && <p className="font-semibold text-sm">{label}</p>}
                  {object?.address && (
                    <div className={"flex flex-row items-center gap-x-2"}>
                      <p className="text-xs text-gray-500">{object.address}</p>
                      {object?.type && (
                        <CompanyInfoTypeComponent company_info_type={object.type} intl={intl}/>
                      )}
                    </div>
                  )}
                  <p className={!object ? "text-sm" : "text-xs"}>
                    {secondaryLabel}
                  </p>
                </article>
              );
            }}
          />
          {userData.role_type !== "resale" && (
            <QuotationEnterpriseSelector
              label={intl.formatMessage({
                id: "select_enterprise",
                defaultMessage: "Seleziona impresa",
              })}
              value={selectedEnterprise}
              loadOptions={async (search: string) => {
                const enterprises = await fetchAgentEnterprises(
                  apiRequest,
                  selectedAgent.value,
                  search
                );
                return mapEnterprisesOptions(
                  enterprises.items
                ) as unknown as UserOption[];
              }}
              onChange={setSelectedEnterprise}
              isDisabled={loadingUsers}
              noOptionsMessage={() =>
                intl.formatMessage({
                  id: "type_value",
                  defaultMessage: "Digita un valore",
                })
              }
              defaultOptions={enterprisesDefaultOptions}
              blocked={userData.role_type === "enterprise"}
            />
          )}

          {/* ---------- Confirm button ---------- */}
          <div className="mt-2">
            {/*@ts-ignore*/}
            <Button
              full
              onClick={async () => {
                // Check if agent and surveys are selected
                if (!selectedAgent?.object)
                  throw new Error("Agent selection is required");
                if (!selectedSurveys.size)
                  throw new Error("At least one survey must be selected");

                const res = await fetchKcolorProducts(
                  Array.from(selectedSurveys)
                );
                setHasKcolorProducts(res);
                if (res && !['resale_tintometer', 'enterprise'].includes(userData.role_type)) {
                  return;
                }
                // Create survey
                await onSubmit();
              }}
              disabled={
                !selectedSurveys.size || // No surveys selected...
                !selectedAgent || // ...or agent selected
                !isValidEnterprise(selectedEnterprise) // ...or invalid enterprise if selected
              }
              submitting={loading}
            >
              <FormattedMessage id="confirm" />
            </Button>
          </div>
        </div>
      )}
      
    </>
  );

  return (
    <>
      {(hasKcolorProducts && !['resale_tintometer', 'enterprise'].includes(userData.role_type)) ? (
        <SelectKcolorPriceModal
          onExit={onDismiss}
          onConfirm={async (value:"kerakoll"|"tintometer") => {
            await onSubmit(value)
          }}
        />
      ) : (
        // @ts-ignore
        <Modal onExit={onDismiss}>
          <h1 className="text-center text-xl font-extrabold my-3">
            <FormattedMessage id="create_quotation" />
          </h1>
          {
            // Show the editor body if there are surveys available, otherwise show a message
            Object.keys(groupedSurveys).length > 0 ? (
              editorBody
            ) : (
              <div className="flex items-center justify-center h-32 mb-4 text-gray-400">
                <FormattedMessage id="no_available_surveys" />
              </div>
            )
          }
        </Modal>
      )}
    </>
  );
};
